import { SdkContext } from '../internal'
import { listItems } from '@/lib/utils'
import type {
  VisitorArrival,
  VisitorHost,
  VisitorInvitation,
  VisitorInvitationInput,
  VisitorListParams,
  VisitorPage,
  VisitorToday
} from '@/types/visitors'

/** F-83 phase 1A: visitors. Paths are written by hand (F-03). */
export class VisitorsResource {
  constructor(private ctx: SdkContext) {}

  async create(spaceId: string, input: VisitorInvitationInput): Promise<VisitorInvitation> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/visitors/spaces/{spaceId}/invitations', hasBody: true },
      { spaceId, body: input }
    )
  }

  async list(spaceId: string, params: VisitorListParams = {}): Promise<VisitorPage> {
    const { status, from, to, q, hostMemberId, page, limit } = params
    const raw: any = await this.ctx.raw(
      { httpMethod: 'GET', path: '/visitors/spaces/{spaceId}/invitations' },
      { spaceId, query: { status, from, to, q: q || undefined, hostMemberId, page, limit } }
    )
    return { items: listItems<VisitorInvitation>(raw), pagination: raw?.pagination ?? null }
  }

  /** What the caller can do in this space: register guests (`whoCanInvite`, group included) and whether they are team. */
  async me(spaceId: string): Promise<{ canInvite: boolean; isTeam: boolean }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/visitors/spaces/{spaceId}/me' }, { spaceId })
  }

  async today(spaceId: string, q?: string): Promise<VisitorToday> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/visitors/spaces/{spaceId}/today' },
      { spaceId, query: { q: q || undefined } }
    )
  }

  async hosts(spaceId: string, q: string): Promise<VisitorHost[]> {
    const result = await this.ctx.call<unknown>(
      { httpMethod: 'GET', path: '/visitors/spaces/{spaceId}/hosts' },
      { spaceId, query: { q } }
    )
    return listItems<VisitorHost>(result)
  }

  async update(id: string, input: VisitorInvitationInput): Promise<VisitorInvitation> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/visitors/invitations/{id}', hasBody: true }, { id, body: input })
  }

  /** Cancels (`status: CANCELED`); nothing is deleted. */
  async cancel(id: string): Promise<VisitorInvitation> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/visitors/invitations/{id}' }, { id })
  }

  async arrive(id: string): Promise<VisitorArrival> {
    return this.ctx.call({ httpMethod: 'POST', path: '/visitors/invitations/{id}/arrive', hasBody: true }, { id, body: {} })
  }

  async undoArrival(id: string): Promise<{ invitation: VisitorInvitation }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/visitors/invitations/{id}/undo-arrival', hasBody: true }, { id, body: {} })
  }
}
